/** @typedef {import('./constants.js').BiaTimeUnit} BiaTimeUnit */

const UNIT_TO_MINUTES = {
    minutes: 1,
    hours: 60,
    days: 60 * 24,
    weeks: 60 * 24 * 7,
};

const UNIT_LABEL_TR = {
    minutes: 'Dakika',
    hours: 'Saat',
    days: 'Gün',
    weeks: 'Hafta',
};

/**
 * @param {number} value
 * @param {BiaTimeUnit} unit
 * @returns {number}
 */
export function durationMinutesFromParts(value, unit) {
    const v = Number(value);
    if (!Number.isFinite(v) || v < 0) return 0;
    const factor = UNIT_TO_MINUTES[unit] ?? 60;
    return Math.round(v * factor);
}

/**
 * Human-readable horizon label. Canonical storage uses English (`locale` omitted / `en`);
 * UI passes the active language so TR users see "2 Saat" instead of "2 Hours".
 *
 * @param {{ value?: number, unit?: BiaTimeUnit }|null|undefined} horizon
 * @param {string} [locale='en']
 * @returns {string}
 */
export function formatHorizonDisplayLabel(horizon, locale = 'en') {
    const value = Number(horizon?.value);
    const unit = horizon?.unit;
    if (!Number.isFinite(value) || !unit) return '';

    const isTr = String(locale || '').toLowerCase().startsWith('tr');
    if (isTr) {
        return `${value} ${UNIT_LABEL_TR[unit] || UNIT_LABEL_TR.hours}`;
    }

    const unitLabels = {
        minutes: value === 1 ? 'Minute' : 'Minutes',
        hours: value === 1 ? 'Hour' : 'Hours',
        days: value === 1 ? 'Day' : 'Days',
        weeks: value === 1 ? 'Week' : 'Weeks',
    };
    return `${value} ${unitLabels[unit] || 'Hours'}`;
}

/**
 * @param {Array<{ id: string, value: number, unit: BiaTimeUnit, sortOrder?: number }>} horizons
 * @returns {Array<{ id: string, value: number, unit: BiaTimeUnit, sortOrder: number, durationMinutes: number, displayLabel: string }>}
 */
export function normalizeTimeHorizons(horizons) {
    const withDuration = (horizons || []).map((h, idx) => {
        const durationMinutes = durationMinutesFromParts(h.value, h.unit);
        return {
            id: String(h.id),
            value: Number(h.value),
            unit: h.unit,
            sortOrder: h.sortOrder ?? idx,
            durationMinutes,
            // Canonical English label for persistence / API payloads
            displayLabel: formatHorizonDisplayLabel(h, 'en'),
        };
    });
    return withDuration.sort((a, b) => {
        if (a.durationMinutes !== b.durationMinutes) {
            return a.durationMinutes - b.durationMinutes;
        }
        return a.sortOrder - b.sortOrder;
    });
}
